import React, { useEffect, useState, useCallback } from 'react';
import { adminGetLogs } from '../../api/api';
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';

const PRIMARY = '#c44569';

const actionColor = (action) => {
  if (action.includes('delete')) return { color: '#dc2626', bg: '#fee2e2' };
  if (action.includes('block'))  return { color: '#d97706', bg: '#fef3c7' };
  if (action.includes('reset') || action.includes('password')) return { color: '#8b5cf6', bg: '#ede9fe' };
  if (action.includes('update')) return { color: '#0284c7', bg: '#e0f2fe' };
  return { color: '#475569', bg: '#f1f5f9' };
};

export default function AdminLogs() {
  const [data, setData]   = useState({ logs: [], total: 0, totalPages: 1 });
  const [page, setPage]   = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminGetLogs({ page, limit: 20 })
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900">Audit Logs</h1>
        <p className="text-slate-500 text-sm mt-1">Track all admin actions</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-7 h-7 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: PRIMARY, borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['#','Action','Actor','Target','Detail','IP','Time'].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.logs.map(l => {
                  const { color, bg } = actionColor(l.action);
                  return (
                    <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-slate-400 text-xs font-medium">{l.id}</td>
                      <td className="px-5 py-3">
                        <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ color, background: bg }}>
                          {l.action}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <p className="font-semibold text-slate-800 text-xs whitespace-nowrap">{l.actor_name || '—'}</p>
                        <p className="text-slate-400 text-xs">{l.actor_email || ''}</p>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {l.target_type ? `${l.target_type} #${l.target_id}` : '—'}
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs max-w-xs truncate">{l.detail || '—'}</td>
                      <td className="px-5 py-3 text-slate-400 text-xs">{l.ip_address || '—'}</td>
                      <td className="px-5 py-3 text-slate-400 text-xs whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
                {data.logs.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400 text-sm">No logs found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data.totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
            <p className="text-xs text-slate-400">Page {data.page} of {data.totalPages} · {data.total} entries</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40 transition"
                style={{ background: '#f1f5f9', color: '#475569' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages}
                className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40 transition"
                style={{ background: '#f1f5f9', color: '#475569' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
